import {
    DestroyRef,
    Directive,
    Input,
    OnChanges,
    OnInit,
    SimpleChanges,
    inject
} from "@angular/core";
import { Subscription } from "rxjs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MatCheckbox } from "@angular/material/checkbox";
import { ProcessingDirective } from "../../../application/directives/processing/processing.directive";
import { HotkeysHostDirective } from "../../../directives/hotkeys/hotkeys-host.directive";

/**
 * Directive that should be applied to `<mat-checkbox>` elements to integrate
 * them with the application processing state and optional hotkey toggling.
 *
 * Composes `ProcessingDirective` (via `hostDirectives`) to automatically disable
 * the checkbox while the application is processing. Composes `HotkeysHostDirective`
 * to support an optional keyboard shortcut that toggles the checkbox.
 *
 * When `checkHotkey` is provided, pressing the registered key combination will
 * toggle the checkbox checked state and emit the `change` event — identical to
 * a user click — unless the checkbox is currently disabled.
 *
 * @example
 * ```html
 * <mat-checkbox mgdCheckbox checkHotkey="alt.c">
 *   Enable feature
 * </mat-checkbox>
 * ```
 *
 * @selector [mgdCheckbox]
 */
@Directive({
    selector: "[mgdCheckbox]",
    hostDirectives: [
        {
            directive: ProcessingDirective,
            inputs: ["disableWhileProcessing", "disabled"]
        },
        HotkeysHostDirective
    ]
})
export class CheckboxDirective implements OnInit, OnChanges {

    /**
     * When set, registers the given hotkey combination to programmatically toggle
     * the checkbox. The toggle is suppressed if the checkbox is currently disabled.
     *
     * @example
     * ```html
     * <mat-checkbox mgdCheckbox checkHotkey="alt.c">Enable feature</mat-checkbox>
     * ```
     */
    @Input() checkHotkey?: string;

    /**
     * Reference to the host MatCheckbox instance
     */
    private readonly _checkbox = inject(MatCheckbox, { self: true });

    /**
     * Reference to the ProcessingDirective composed via hostDirectives
     */
    private readonly _processing = inject(ProcessingDirective, { self: true });

    /**
     * Reference to the HotkeysHostDirective composed via hostDirectives
     */
    private readonly _hotkeys = inject(HotkeysHostDirective, { self: true });

    /**
     * Reference used to clean up subscriptions when the directive is destroyed
     */
    private readonly _destroyRef = inject(DestroyRef);

    /**
     * Tracks the subscription to the hotkey shortcut, so it can be unsubscribed when the hotkey changes
     */
    private _hotkeySubscription?: Subscription;

    /**
     * Angular OnInit lifecycle hook.
     *
     * Registers the `checkHotkey` shortcut if provided.
     */
    ngOnInit(): void {
        this._registerHotkey();
    }

    /**
     * Angular OnChanges lifecycle hook.
     *
     * Re-registers the hotkey shortcut when `checkHotkey` changes after init.
     *
     * @param changes
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (changes.checkHotkey && !changes.checkHotkey.firstChange) {
            this._registerHotkey();
        }
    }

    /**
     * Unsubscribes from any existing hotkey subscription and re-registers if `checkHotkey` is set.
     * Called from `ngOnInit` and from `ngOnChanges` when `checkHotkey` changes after init.
     */
    private _registerHotkey(): void {
        this._hotkeySubscription?.unsubscribe();
        this._hotkeySubscription = undefined;

        if (!this.checkHotkey) {
            return;
        }

        this._hotkeySubscription = this._hotkeys.addShortcut$({
            keys: this.checkHotkey,
            allowIn: ["INPUT", "SELECT", "TEXTAREA"]
        })
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe(() => {
                if (!this._processing.disabled) {
                    this._checkbox.toggle();
                }
            });
    }
}
